/**
 * evaluateConstraint
 * Evalúa expresiones de constraint (ODK-like) desde string contra un valor actual
 * y los datos completos del formulario.
 *
 * Soporta:
 * - Comparadores: =, ==, !=, <, >, <=, >=
 * - Lógicos: and, or, not(...)
 * - Placeholders: . (valor actual), ${campo}
 * - Funciones: selected(fieldOrDot, 'valor'), regex(., 'patrón'), today()
 * - Paréntesis
 */

import { normalizeFieldToList, equalsFlexible, compareValues, getField } from './expressionValueHelpers';

// Utilidades similares a evaluateMenuRelevant
function isArray(val) { return Array.isArray(val); }
function isObject(val) { return val !== null && typeof val === 'object' && !Array.isArray(val); }

function formatToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Tokenizador simple para constraints ODK
function tokenize(expr) {
  const tokens = [];
  let i = 0;
  const len = expr.length;
  const isAlpha = c => /[A-Za-z_]/.test(c);
  const isDigit = c => /[0-9]/.test(c);
  while (i < len) {
    const ch = expr[i];
    if (/\s/.test(ch)) { i++; continue; }
    // Field ${name}
    if (ch === '$' && expr[i+1] === '{') {
      let j = i + 2;
      let name = '';
      while (j < len && expr[j] !== '}') { name += expr[j]; j++; }
      tokens.push({ type: 'FIELD', value: name.trim() });
      i = j + 1;
      continue;
    }
    if (ch === '.') { tokens.push({ type: 'DOT', value: '.' }); i++; continue; }
    if (ch === '(') { tokens.push({ type: 'LPAREN' }); i++; continue; }
    if (ch === ')') { tokens.push({ type: 'RPAREN' }); i++; continue; }
    if (ch === ',') { tokens.push({ type: 'COMMA' }); i++; continue; }
    // Operators
    const two = expr.slice(i, i+2);
    if (two === '<=' || two === '>=' || two === '!=' || two === '==') { tokens.push({ type: 'OP', value: two }); i += 2; continue; }
    if (ch === '<' || ch === '>' || ch === '=') { tokens.push({ type: 'OP', value: ch }); i++; continue; }
    // String literal
    if (ch === '\'' || ch === '"') {
      const quote = ch; let j = i + 1; let val = '';
      while (j < len && expr[j] !== quote) { val += expr[j]; j++; }
      tokens.push({ type: 'STRING', value: val });
      i = j + 1; continue;
    }
    // Number
    if (isDigit(ch)) {
      let j = i; let numStr = '';
      while (j < len && /[0-9.]/.test(expr[j])) { numStr += expr[j]; j++; }
      tokens.push({ type: 'NUMBER', value: Number(numStr) });
      i = j; continue;
    }
    // Identifiers: selected, regex, today, and, or, not
    if (isAlpha(ch)) {
      let j = i; let id = '';
      while (j < len && /[A-Za-z_]/.test(expr[j])) { id += expr[j]; j++; }
      tokens.push({ type: 'IDENT', value: id.toLowerCase() });
      i = j; continue;
    }
    // Cualquier otro char se ignora
    i++;
  }
  return tokens;
}

function Parser(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = () => tokens[pos++];
  const match = (type, value) => {
    const t = peek();
    if (!t || t.type !== type) return false;
    if (value !== undefined && t.value !== value) return false;
    pos++; return true;
  };

  function parseValue() {
    const t = peek();
    if (!t) return { type: 'literal', value: undefined };
    // Paréntesis de agrupación: (expr) — no confundir con los de una función,
    // que ya consume cada rama de IDENT más abajo.
    if (t.type === 'LPAREN') {
      consume();
      const inner = parseOr();
      match('RPAREN');
      return inner;
    }
    if (t.type === 'DOT') { consume(); return { type: 'current' }; }
    if (t.type === 'FIELD') { const n = consume(); return { type: 'field', name: n.value }; }
    if (t.type === 'STRING') { const s = consume(); return { type: 'literal', value: s.value }; }
    if (t.type === 'NUMBER') { const n = consume(); return { type: 'literal', value: n.value }; }
    if (t.type === 'IDENT') {
      const id = peek().value;
      if (id === 'today') { consume(); return { type: 'literal', value: formatToday() }; }
      if (id === 'selected') {
        consume(); match('LPAREN');
        const arg1 = parseValue(); match('COMMA');
        const arg2 = parseValue(); match('RPAREN');
        return { type: 'function', name: 'selected', args: [arg1, arg2] };
      }
      if (id === 'regex') {
        consume(); match('LPAREN');
        const arg1 = parseValue(); match('COMMA');
        const arg2 = parseValue(); match('RPAREN');
        return { type: 'function', name: 'regex', args: [arg1, arg2] };
      }
      if (id === 'not') {
        consume(); match('LPAREN');
        const inner = parseOr();
        match('RPAREN');
        return { type: 'logical_not', expr: inner };
      }
    }
    // Fallback
    consume();
    return { type: 'literal', value: undefined };
  }

  function parseComparison() {
    let left = parseValue();
    const t = peek();
    if (t && t.type === 'OP') {
      const op = consume().value;
      const right = parseValue();
      return { type: 'comparison', left, op, right };
    }
    return left;
  }

  function parseNot() {
    const node = parseComparison();
    return node;
  }

  function parseAnd() {
    let node = parseNot();
    while (peek() && peek().type === 'IDENT' && peek().value === 'and') {
      consume();
      const right = parseNot();
      node = { type: 'logical', op: 'and', items: [node, right] };
    }
    return node;
  }

  function parseOr() {
    let node = parseAnd();
    while (peek() && peek().type === 'IDENT' && peek().value === 'or') {
      consume();
      const right = parseAnd();
      node = { type: 'logical', op: 'or', items: [node, right] };
    }
    return node;
  }

  function parseExpression() {
    return parseOr();
  }

  return { parseExpression };
}

function evalNode(node, ctx, toggles) {
  if (!node || typeof node !== 'object') return true;
  const type = node.type;
  if (type === 'logical') {
    const op = node.op || 'and';
    const items = Array.isArray(node.items) ? node.items : [];
    if (toggles && toggles.logical === false) return true;
    if (op === 'and') return items.every(it => evalNode(it, ctx, toggles));
    if (op === 'or') return items.some(it => evalNode(it, ctx, toggles));
    return items.every(it => evalNode(it, ctx, toggles));
  }
  if (type === 'logical_not') {
    if (toggles && toggles.logical === false) return true;
    return !evalNode(node.expr, ctx, toggles);
  }
  if (type === 'function') {
    const name = (node.name || '').toLowerCase();
    if (name === 'selected') {
      if (toggles && toggles.selected === false) return true;
      const leftVal = evalValue(node.args[0], ctx);
      const targetVal = evalValue(node.args[1], ctx);
      const list = normalizeFieldToList(leftVal);
      const targetStr = String(targetVal ?? '').trim();
      return list.some(item => equalsFlexible(item, targetStr));
    }
    if (name === 'regex') {
      if (toggles && toggles.regex === false) return true;
      const val = String(evalValue(node.args[0], ctx) ?? '');
      const pattern = String(evalValue(node.args[1], ctx) ?? '');
      try {
        const re = new RegExp(pattern);
        return re.test(val);
      } catch { return false; }
    }
    return false;
  }
  if (type === 'comparison') {
    if (toggles && toggles.comparisons === false) return true;
    const leftVal = evalValue(node.left, ctx);
    const rightVal = evalValue(node.right, ctx);
    return compareValues(node.op || '=', leftVal, rightVal);
  }
  // value-only treated as truthy test (non-empty)
  const v = evalValue(node, ctx);
  return Boolean(v);
}

function evalValue(node, ctx) {
  if (!node || typeof node !== 'object') return undefined;
  if (node.type === 'current') return ctx.currentValue;
  if (node.type === 'field') return getField(ctx.datos, node.name);
  if (node.type === 'literal') return node.value;
  if (node.type === 'function' || node.type === 'comparison' || node.type === 'logical' || node.type === 'logical_not') {
    return evalNode(node, ctx, ctx.toggles);
  }
  return undefined;
}

export default function evaluateConstraint(exprString, currentValue, datosFormulario, question = {}, toggles = {}) {
  try {
    if (!exprString || typeof exprString !== 'string') return true;
    const tokens = tokenize(exprString);
    const parser = Parser(tokens);
    const ast = parser.parseExpression();

    // Si está deshabilitada la validación de fechas y la expresión usa today(), omitir
    const usesToday = exprString.toLowerCase().includes('today()');
    const isDateType = (question?.type === 'date' || question?.type === 'datetime');
    const mergedToggles = {
      dates: true,
      regex: true,
      selected: true,
      comparisons: true,
      logical: true,
      ...toggles
    };
    if (mergedToggles.dates === false && (usesToday || isDateType)) return true;

    // Si la expresión no produjo nada evaluable (sintaxis no soportada), fail-open:
    // considerarla válida en vez de bloquear el guardado.
    if (ast && ast.type === 'literal' && ast.value === undefined) return true;

    const ctx = { currentValue, datos: datosFormulario || {}, toggles: mergedToggles };
    return Boolean(evalNode(ast, ctx, mergedToggles));
  } catch (e) {
    // En caso de error en parsing/evaluación, considerar válido para no bloquear
    return true;
  }
}